/** Provider-neutral payment data and request contracts shared by feature consumers. */
import { IServerOrder } from '@rbi-ctg/menu';
import {
  CartPaymentCardType,
  IApplePay,
  IBillingAddressInput,
  IGooglePay,
} from 'generated/graphql-gateway';
import { ISOs } from 'utils/form/constants';

export type CardType = keyof typeof CartPaymentCardType;

export { CartPaymentCardType as CardTypes };

/**
 * These card types are the ones supported by our Apple Pay plugin (ios/App/App/ApplePayPlugin.swift)
 */
export enum ApplePayCardTypes {
  AMEX = 'AmEx',
  DISCOVER = 'Discover',
  MASTERCARD = 'MasterCard',
  VISA = 'Visa',
}

export enum PaymentType {
  CASH = 'CASH',
  DIGITAL = 'DIGITAL',
  GIFT = 'GIFT',
}

export enum CurrentSelectedMethodType {
  ADD_NEW_PAYMENT_METHOD = 'addNewPaymentMethod',
  ADD_NEW_GIFT_CARD = 'addNewGiftCard',
  ADD_NEW_CARD = 'addNewCard',
  SELECTED_PAYMENT_METHOD_OPTION = 'selectedPaymentMethodOption',
}

export interface ICredit {
  alias: string;
  /**
   * The BE does not currently standardize card type, for example upper case vs lower case.
   * Eventually we want to align the FE and BE.
   *
   * TODO: Use type from `IUserAccountsQuery` and not force our own.
   */
  cardType: CartPaymentCardType | string;
  expiryMonth: string;
  expiryYear: string;
  panToken: string;
}

export interface IPrepaid {
  alias: string;
  cardNumber: string;
  currentBalance: number;
  expiryMonth: string;
  expiryYear: string;
  feFormattedCurrentBalance: number | null;
  shuffledCardNumber: string;
}

export interface IPaymentMethod {
  accountIdentifier: string | null;
  fdAccountId: string | null;
  orbitalIdentifier: string | null;
  credit: ICredit | null;
  selected?: boolean;
  prepaid: IPrepaid | null;
  // make cash required eventually
  cash?: boolean | null;
  cashapp: boolean | null;
  paypal: boolean | null;
  venmo: boolean | null;
}

export type PaymentMethodCategories = {
  selectedMethod: IPaymentMethod | undefined;
  unSelectedMethods: IPaymentMethod[];
  prepaidPaymentMethod: IPaymentMethod | undefined;
};

export interface IAddAccount {
  addAccount: IPaymentMethod;
}

export interface IAddress {
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
  unitNumber?: string;
}

export interface IAddPaymentMethodOptions {
  skipErrorDialogOnError?: boolean;
  isReloadMethod?: boolean;
}

export type GetPaymentMethodsErrorBehavior = 'show-dialog' | 'log-only';

export type GetPaymentMethodsSource =
  | 'add-payment-method-success'
  | 'gift-card-balance-visible'
  | 'merge-prepaid-card-balances'
  | 'order-commit-success'
  | 'prepaid-reload-commit-failed'
  | 'prepaid-reload-apple-pay'
  | 'prepaid-reload-google-pay'
  | 'prepaid-reload-standard';

export interface IGetPaymentMethodsOptions {
  errorBehavior?: GetPaymentMethodsErrorBehavior;
  source: GetPaymentMethodsSource;
}

export interface IDeleteAccount {
  deleteAccount: boolean;
}

export interface IGetAccounts {
  userAccounts?: {
    count?: number;
    accounts?: IPaymentMethod[];
  };
}

// @todo validate type
export interface IGetNonce {
  token: {
    tokenId: string;
  };
}

export interface IApplePayDetails extends IApplePay {
  signature: string;
  applicationData: string;
  data: string;
  decryptAlias: string;
  ephemeralPublicKey: string;
  formatted: string;
  paymentData: string;
  primary: boolean;
  publicKeyHash: string;
  transactionId: string;
  type: string;
  version: string;
  billingAddress: IBillingAddressInput;
  PaymentMethodData?: IPaymentMethodData;
}

export interface IApplePayCartEntry {
  /**
   * Checkout cart data can omit display fields at the API boundary. Keep those
   * fields optional here, but validate every entry before presenting Apple Pay.
   */
  name?: string;
  price?: number;
  quantity: number;
}

export interface IGooglePayDetails extends IGooglePay {
  signature: string;
  data: string;
  formatted: string;
  primary: boolean;
  type: string;
  version: string;
  billingAddress: IBillingAddressInput;
  PaymentMethodData?: IPaymentMethodData;
  paymentData: string;
}

export interface IBraintreeDetails {
  /**
   * Stringified object representing additional details related to the payment and user.
   * It is used for Forter fraud prevention and is inconsistent between platforms and users.
   * The BE will parse this string and handle accordingly based on the data available.
   */
  details: string;
  /**
   * Unique one-time identifier representing the device used to make the payment as a result
   * of the successful PayPal/Venmo payment method selection. This value is used by Braintree
   * for fraud prevention.
   */
  deviceData: string;
  /**
   * Unique one-time token representing the payment method as a result of a successful PayPal/Venmo
   * payment method selection. This value is used  to finalize the payment in the  BE and is then discarded.
   */
  nonce: string;
}

export interface IPayPalDetails extends IBraintreeDetails {}

export interface IVenmoDetails extends IBraintreeDetails {}

export interface ICashAppDetails {
  virtualCard: {
    cardType: string;
    cardNumber: string;
    cvc: string;
    expiry: string;
    expiryMonth: string;
    expiryYear: string;
  };
  status: string;
  token: string;
  cashtag: string;
  fdAccessToken: string;
  fdNonce: string;
}

interface IPaymentMethodData {
  paymentType: string;
  displayName: string;
}

export interface IReloadPrepaidCard {
  countryCode: string;
  fundValue: number;
  prepaidBalance: number;
  fdAccountId?: string | null;
  prepaidFdAccountId?: string | null;
  currencyCode?: string;
  cardType?: string | null;
  applePayDetails?: IApplePayDetails;
  googlePayDetails?: IGooglePayDetails;
}

export interface IReloadPrepaidCardConfig {
  swallowError?: boolean;
}

export interface IReloadDetails {
  prepaidsReload: {
    currentBalance: number;
    fdAccountId: string;
    fdCorrelationId: string;
    transactionId: string;
  };
}

/**
 * Vendor specific payment state
 */
export type PaymentState = IPaymentState;

export interface IPaymentDetails {
  accountIdentifier?: string;
  encryptedCN?: string;
  encryptedEM?: string;
  encryptedEY?: string;
  encryptedSC?: string;
  fdAccessToken?: string;
  fdNonce?: string;
  creditType?: CartPaymentCardType | null;
  fraudPreventionValues?: IFraudPreventionValues;
  applePayDetails?: IApplePayDetails;
  googlePayDetails?: IGooglePayDetails;
  paypalDetails?: IPayPalDetails;
  venmoDetails?: IVenmoDetails;
  cashappDetails?: ICashAppDetails;
  saveCard?: boolean;
  /**
   * Vendor specific payment state
   */
  state?: PaymentState;
}

export interface IRevaultInformation {
  isInvalid: boolean;
  isExpired: boolean;
}

export interface IPaymentErrors {
  nameOnCard: string;
  cardNumber: string;
  cardType: string;
  cvv: string;
  expiry: string;
  billingStreetAddress: string;
  billingApt: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  billingCountry?: string;
  giftCardNumber?: string;
  didAttemptSubmit?: boolean;
}

export interface IPaymentState {
  nameOnCard: string;
  cardNumber: string;
  isCardNumberValid: boolean;
  isExpiryValid: boolean;
  cardType: CartPaymentCardType | null;
  cvv: string | null;
  expiry: string | null;
  billingStreetAddress: string | null;
  billingApt: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingZip: string;
  billingCountry: ISOs | null;
  billingAddressSameAsDelivery: boolean;
  saveCard: boolean;
}

export interface IGiftCardPaymentErrors {
  cardNumber: string;
}
export type CCFormField = keyof Omit<
  IPaymentState,
  'isCardNumberValid' | 'isExpiryValid' | 'cardType'
>;

export interface IGiftCardPaymentState {
  cardNumber: string;
  isCardNumberValid: boolean;
  saveCard: boolean;
}

/**
 * "Payment Processor" billing address.
 *
 * Note: There is no such thing as a payment processor billing
 * address. Check the FirstData and payment gateway
 * spec to ensure format is compliant.
 *
 * https://firstdatanp-ucomgateway.apigee.io/apis/ucomaccountservices/index
 *
 */
export type IPaymentProcessorBillingAddress = {
  unitNumber: string | null;
  locality: string | null;
  postalCode: string;
  region: string | null;
  streetAddress: string | null;
  country: ISOs | null;
};

export interface ICCExpiry {
  month: string;
  year: string;
}

export interface IPaymentPayload {
  billingAddress: IPaymentProcessorBillingAddress;
  cardNumber: string;
  cardType: string;
  expiryDate: ICCExpiry | null;
  nameOnCard: string;
  securityCode: string | null;
}

export interface IFraudPreventionValues {
  billingAddress: IPaymentProcessorBillingAddress;
  fullName: string;
  ccBin?: string | null;
  ccLast4?: string | null;
  expiry: string | null;
}

export enum paymentErrorType {
  genericError = 'genericError',
  cashAppGeneric = 'cashAppGenericError',
  cashAppDeclined = 'cashAppDeclined',
  cashAppTimeout = 'cashAppTimeout',
  applePayGeneric = 'applePayGeneric',
  applePayCancelled = 'applePayCancelled',
  googlePayGeneric = 'googlePayGeneric',
  googlePayCancelled = 'googlePayCancelled',
  storeUnavailable = 'storeUnavailable',
  firstDataCreditCardGeneric = 'firstDataCreditCardGeneric',
}

export type RequestPaymentInput = {
  firsDataCard?: {
    paymentValues: IPaymentState | undefined;
  };
  cashapp?: {
    tip: number;
    total: number;
    tax: number;
    onRedirectComplete?: (details: IPaymentDetails) => Promise<void> | void;
    onRedirectStateChange?: (isPending: boolean) => void;
  };
  applePay?: {
    billingCountry: ISOs;
    cartEntries?: IApplePayCartEntry[];
    tip: number;
    reloadAmount: number;
    isForPrepaidReload?: boolean;
    serverOrder?: IServerOrder | null | undefined;
  };
  googlePay?: {
    billingCountry: ISOs;
    total: number;
    reloadAmount: number;
    isForPrepaidReload?: boolean;
  };
};

export class PaymentRequestError extends Error {
  constructor(public readonly errorType: paymentErrorType) {
    super(`Payment request failed: ${errorType}`);
    this.name = 'PaymentRequestError';
  }
}

export type RequestPaymentResult = {
  requestPayment: () => Promise<IPaymentDetails | undefined>;
};

export type RequestPaymentFn = (input: RequestPaymentInput) => RequestPaymentResult;
